import React, { useCallback, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Send,
  AlertCircle,
  Stethoscope,
  Award,
  CheckCircle,
  FileSignature,
  Circle,
  Loader2,
  ClipboardCheck,
  Scale,
  Microscope,
  AlertTriangle,
  Clock
} from 'lucide-react';
import { GenericCard } from 'components/display/Card/Card';
import { quotesAPI } from 'services/swagger-api';
import { Button } from 'components/ui/Button';
import { CardsSection } from 'components/layout/Section/CardsSection';
import {
  useBorrowerGlobalInfos,
  useBorrowerCardStatuses,
  useIsMedicalSurveyCardVisible,
  useIsMedicalSurveyResponseCardVisible,
  useIsMedicalFormCardVisible,
  useCanSendMedicalForm,
  useMedicalFormDisabledReason,
  useMedicalFormProcessInfo,
  useIsApprovedAgreementCardVisible
} from '../../store';
import { CardState } from '../../types/cardState';
import { mapApiStatusToCardState } from '../../types/api';
import {
  CardStatusDto,
  BorrowerProcessStepDto,
  BorrowerMedicalProcessDto,
  MedicalSurveyResponseProcessDto
} from 'api/quote';
import client from 'app/client';
import { useIsCertificateCardVisible } from '../../store/borrowerSelectors';
import { useBorrowerProcessSync } from '../../hooks/useBorrowerProcessSync';

interface ProcessTrackerProps {
  borrowerId?: string;
  refresh: () => void;
}

// Configuration d'affichage basée sur CardState
const cardStateConfig: Record<
  CardState,
  {
    badge: string;
    label: string;
    dot: string;
    icon: React.ElementType;
  }
> = {
  [CardState.VALID]: {
    badge: 'bg-green-100 text-green-700',
    label: 'Complété',
    dot: 'bg-green-500',
    icon: CheckCircle2
  },
  [CardState.IN_PROGRESS]: {
    badge: 'bg-amber-100 text-amber-700',
    label: 'En cours',
    dot: 'bg-amber-500',
    icon: Send
  },
  [CardState.CRITICAL]: {
    badge: 'bg-red-100 text-red-700',
    label: 'Action requise',
    dot: 'bg-red-500',
    icon: AlertCircle
  }
};

// Helper pour formater les dates avec heures
const formatDate = (date?: Date): string => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

interface CardConfig {
  id: string;
  title: string;
  icon: React.ElementType;
  cardStatus: CardStatusDto | null | undefined;
  processInfo: BorrowerProcessStepDto | BorrowerMedicalProcessDto | MedicalSurveyResponseProcessDto | undefined;
  isVisible: boolean;
  showActionButton?: boolean;
  showGeneratingAnimation?: boolean;
  actionButtonLabel?: string;
  onAction?: () => void;
  spanFullWidth?: boolean;
}

// Animation de génération en cours avec points animés
const GeneratingAnimation: React.FC = () => (
  <div className="flex items-center justify-center h-full">
    <div className="flex items-center gap-3 text-primary">
      <Loader2 className="w-5 h-5 animate-spin" />
      <span className="text-sm font-medium">
        Génération en cours
      </span>
    </div>
  </div>
);

// Ligne d'étape avec icône à gauche et date à droite
const StepRow: React.FC<{
  label: string;
  isComplete: boolean;
  date?: Date;
  icon: React.ElementType;
}> = ({ label, isComplete, date, icon: Icon }) => (
  <div className="flex items-center justify-between py-1.5">
    <div className="flex items-center gap-2">
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center ${isComplete ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
          }`}
      >
        <Icon className="w-3 h-3" />
      </div>
      <span
        className={`text-sm ${isComplete ? 'text-gray-700 font-medium' : 'text-gray-400'
          }`}
      >
        {label}
      </span>
    </div>
    <span
      className={`text-sm ${isComplete ? 'text-gray-600' : 'text-gray-300'}`}
    >
      {isComplete && date ? formatDate(date) : 'En attente'}
    </span>
  </div>
);

// Composant pour afficher les informations de process en liste verticale
const ProcessInfoDisplay: React.FC<{
  processInfo: BorrowerProcessStepDto | BorrowerMedicalProcessDto | undefined;
  isMedical?: boolean;
}> = ({ processInfo, isMedical = false }) => {
  if (!processInfo) {
    return (
      <div className="flex items-center justify-center py-2 text-gray-400">
        <Circle className="w-4 h-4 mr-2" />
        <span className="text-sm">En attente</span>
      </div>
    );
  }

  const medicalInfo = isMedical
    ? (processInfo as BorrowerMedicalProcessDto)
    : null;

  return (
    <div className="flex flex-col divide-y divide-gray-100">
      <StepRow
        label="Envoyé"
        isComplete={processInfo.isSend}
        date={processInfo.sendDate}
        icon={Send}
      />
      {isMedical && medicalInfo && (
        <StepRow
          label="Complété"
          isComplete={medicalInfo.isComplete}
          date={medicalInfo.completeDate}
          icon={CheckCircle}
        />
      )}
      {isMedical && medicalInfo && (
        <StepRow
          label="Envoyé pour signature"
          isComplete={medicalInfo.isSendForSignature ?? false}
          date={medicalInfo.sendForSignatureDate}
          icon={Send}
        />
      )}
      <StepRow
        label="Signé"
        isComplete={processInfo.isSign}
        date={processInfo.signDate}
        icon={FileSignature}
      />
    </div>
  );
};

// Composant pour afficher les informations de la réponse au questionnaire médical
const MedicalSurveyResponseInfoDisplay: React.FC<{
  processInfo: MedicalSurveyResponseProcessDto | undefined;
  /**
   * Vrai quand le dossier inclut une fiche médicale complémentaire (Mutlog
   * FM3..FM11). Dans ce cas le BPA n'est pas conditionné par la seule
   * signature du QS — il faut AUSSI la réception des formalités médicales —
   * donc on remplace le message du back (qui ne mentionne que le QS) par un
   * texte qui couvre les deux conditions.
   */
  requiresMedicalForm?: boolean;
}> = ({ processInfo, requiresMedicalForm }) => {
  const backMessage = processInfo?.explanatoryMessage;
  const displayedMessage = requiresMedicalForm
    ? "Le Bon Pour Accord (BPA) sera disponible et envoyé pour signature au souscripteur une fois le questionnaire de santé signé et la fiche médicale complémentaire reçue."
    : backMessage;

  return (
    <div className="flex flex-col divide-y divide-gray-100">
      <StepRow
        label={processInfo?.statusLabel || "Traité"}
        isComplete={processInfo?.isProcessed ?? false}
        date={processInfo?.processedDate ?? undefined}
        icon={CheckCircle}
      />
      {processInfo?.isProcessed && displayedMessage && (
        <div className="py-2">
          <p className="text-sm text-gray-500 leading-relaxed">
            {displayedMessage}
          </p>
        </div>
      )}
    </div>
  );
};

// Composant pour afficher les informations du certificat (Envoyé + Reçu)
const CertificateInfoDisplay: React.FC<{
  processInfo: BorrowerProcessStepDto | undefined;
  partnerCaseStatus?: string | null;
  partnerCaseStatusDetail?: string | null;
}> = ({ processInfo, partnerCaseStatus, partnerCaseStatusDetail }) => {
  const statusDetail = partnerCaseStatusDetail || partnerCaseStatus;

  if (!processInfo) {
    return (
      <div className="flex flex-col divide-y divide-gray-100">
        <div className="flex items-center justify-center py-2 text-gray-400">
          <Circle className="w-4 h-4 mr-2" />
          <span className="text-sm">En attente</span>
        </div>
        {statusDetail && (
          <div className="px-3 py-2 text-xs text-amber-600 bg-amber-50 rounded-b">
            {statusDetail}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col divide-y divide-gray-100">
      <StepRow
        label="Envoyé"
        isComplete={processInfo.isSend}
        date={processInfo.sendDate}
        icon={Send}
      />
      {statusDetail && !processInfo.isSend && (
        <div className="px-3 py-2 text-xs text-amber-600 bg-amber-50 rounded-b">
          {statusDetail}
        </div>
      )}
    </div>
  );
};

/**
 * Body de la carte "Fiche médicale complémentaire".
 *
 * Affiche :
 * - la liste des examens à fournir (filtrée depuis `medicalDocuments`),
 * - si l'envoi n'a pas eu lieu : un bouton "Envoyer la fiche au souscripteur"
 *   (désactivé tant que le back ne l'autorise pas, avec la raison en dessous),
 * - si l'envoi a eu lieu : deux StepRow alignés sur le pattern QS — "Lien
 *   envoyé" (sendDate) + "Documents reçus" (receivedDate enrichi du compteur),
 * - si le lien est bloqué (3 échecs DDN) ou expiré (>30j) : un bandeau
 *   d'erreur + bouton pour regénérer un nouveau lien (= même endpoint /envoyer
 *   que l'envoi initial ; le back se charge d'invalider l'ancien token).
 */
const MedicalFormDisplay: React.FC<{
  exams: string[];
  processInfo?: {
    isSend: boolean;
    isReceived: boolean;
    resultsCount: number;
    sendDate?: Date | null;
    receivedDate?: Date | null;
    isBlocked?: boolean;
    isExpired?: boolean;
  };
  cardStatus?: CardStatusDto;
  canSend: boolean;
  isSending: boolean;
  disabledReason?: string;
  onSend: () => void;
}> = ({
  exams,
  processInfo,
  cardStatus,
  canSend,
  isSending,
  disabledReason,
  onSend
}) => {
  const isSent = processInfo?.isSend ?? false;
  const isReceived = processInfo?.isReceived ?? false;
  const resultsCount = processInfo?.resultsCount ?? 0;
  const isBlocked = processInfo?.isBlocked ?? false;
  const isExpired = processInfo?.isExpired ?? false;
  const isErrored = isBlocked || isExpired;
  const isCardDisabled = cardStatus?.isDisabled ?? false;

  // Le bouton d'envoi initial est désactivé quand le back l'interdit OU
  // quand l'envoi est déjà fait — sauf cas d'erreur (isBlocked / isExpired),
  // où on doit pouvoir regénérer un nouveau lien même si "techniquement"
  // un envoi existe déjà.
  const buttonDisabled =
    !canSend ||
    isSending ||
    isCardDisabled ||
    (isSent && !isErrored);

  // Label "Documents reçus" enrichi du nombre quand on a effectivement reçu
  // quelque chose. Tant que isReceived === false on garde le label nu pour
  // ne pas afficher "(0)" trompeur.
  const receivedLabel =
    isReceived && resultsCount > 0
      ? `Documents reçus (${resultsCount})`
      : 'Documents reçus';

  return (
    <div className="flex flex-col gap-4">
      {exams.length > 0 ? (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-gray-700">
            Examens médicaux à fournir :
          </p>
          <ul className="flex flex-col gap-1 list-disc list-inside text-sm text-gray-600">
            {exams.map((exam, idx) => (
              <li key={`${idx}-${exam}`}>{exam}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex items-center text-gray-400">
          <Circle className="w-4 h-4 mr-2" />
          <span className="text-sm">Aucun examen à fournir</span>
        </div>
      )}

      {isSent ? (
        // Cas "déjà envoyé" : tracking aligné sur le pattern des autres cartes
        // (StepRow avec ronds verts + date à droite).
        <div className="flex flex-col divide-y divide-gray-100 border-t pt-1">
          <StepRow
            label="Lien envoyé au souscripteur"
            isComplete={isSent}
            date={processInfo?.sendDate ?? undefined}
            icon={Send}
          />
          <StepRow
            label={receivedLabel}
            isComplete={isReceived}
            date={processInfo?.receivedDate ?? undefined}
            icon={CheckCircle}
          />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Button
            color="secondary"
            round
            onClick={onSend}
            disabled={buttonDisabled}
          >
            {isSending
              ? 'Envoi en cours…'
              : 'Envoyer la fiche au souscripteur'}
          </Button>
          {buttonDisabled && disabledReason && (
            <p className="text-xs text-amber-600 text-center">
              {disabledReason}
            </p>
          )}
        </div>
      )}

      {/* Bandeau erreur + bouton de régénération. On l'affiche EN PLUS du
          tracking ci-dessus pour que le courtier garde le contexte (date
          d'envoi initial qui a foiré). Le bouton réutilise onSend : côté
          back c'est le même endpoint /envoyer qui se charge d'invalider
          l'ancien token et d'en émettre un nouveau. */}
      {isSent && isErrored && (
        <MedicalFormErroredBanner
          kind={isBlocked ? 'blocked' : 'expired'}
          onRegenerate={onSend}
          isSending={isSending}
          regenerateDisabled={
            !canSend || isSending || isCardDisabled
          }
        />
      )}
    </div>
  );
};

/**
 * Bandeau rouge affiché quand le lien d'origine est mort (bloqué après 3
 * mauvaises DDN, ou expiré au bout de 30 jours sans confirmation). On
 * propose au courtier de regénérer le lien — c'est la seule action utile
 * dans cet état.
 */
const MedicalFormErroredBanner: React.FC<{
  kind: 'blocked' | 'expired';
  onRegenerate: () => void;
  isSending: boolean;
  regenerateDisabled: boolean;
}> = ({ kind, onRegenerate, isSending, regenerateDisabled }) => {
  const isBlocked = kind === 'blocked';
  const Icon = isBlocked ? AlertTriangle : Clock;
  const title = isBlocked ? 'Lien bloqué' : 'Lien expiré';
  const description = isBlocked
    ? "Le souscripteur a saisi 3 fois une date de naissance incorrecte. Pour des raisons de sécurité, le lien d'origine a été verrouillé."
    : "Aucune confirmation depuis plus de 30 jours, le lien d'origine est expiré.";

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
      <div className="flex items-start gap-3">
        <Icon
          aria-hidden="true"
          className="w-5 h-5 mt-0.5 text-red-600 flex-shrink-0"
        />
        <div>
          <p className="font-semibold text-red-800">{title}</p>
          <p className="text-sm text-red-700">{description}</p>
        </div>
      </div>
      <div className="flex justify-center">
        <Button
          color="secondary"
          round
          onClick={onRegenerate}
          disabled={regenerateDisabled}
        >
          {isSending ? 'Envoi en cours…' : 'Renvoyer un nouveau lien'}
        </Button>
      </div>
    </div>
  );
};

/**
 * Mapping local code raison back → texte humain affiché sous le bouton désactivé.
 * Si le back ajoute une nouvelle clé non listée ici, on retombe sur le code brut
 * (lisible mais en MAJ_SNAKE_CASE — visible immédiatement comme une trad manquante).
 */
const MEDICAL_FORM_DISABLED_REASONS: Record<string, string> = {
  MEDICAL_SURVEY_NOT_SIGNED:
    'Le questionnaire santé doit être signé avant l’envoi de la fiche médicale.',
  APPROVED_AGREEMENT_NOT_SIGNED:
    'Le bon pour accord doit être signé avant l’envoi de la fiche médicale.',
  MEDICAL_SURVEY_RESPONSE_NOT_PROCESSED:
    'La réponse au questionnaire médical doit être traitée avant l’envoi de la fiche médicale.'
};

const isDevelopment = globalThis.process?.env?.NODE_ENV === 'development';

const BorrowerProcessTracker: React.FC<ProcessTrackerProps> = ({
  borrowerId,
  refresh
}) => {
  const globalInfos = useBorrowerGlobalInfos();
  const cardStatuses = useBorrowerCardStatuses();
  const isMedicalSurveyVisible = useIsMedicalSurveyCardVisible();
  const isMedicalSurveyResponseVisible = useIsMedicalSurveyResponseCardVisible();
  const isMedicalFormVisible = useIsMedicalFormCardVisible();
  const canSendMedicalForm = useCanSendMedicalForm();
  const medicalFormDisabledReason = useMedicalFormDisabledReason();
  const medicalFormProcessInfo = useMedicalFormProcessInfo();
  const isApprovedAgreementVisible = useIsApprovedAgreementCardVisible();
  const isCertificateVisible = useIsCertificateCardVisible();

  const [isSendingMedicalForm, setIsSendingMedicalForm] = useState(false);

  // Examens à afficher dans la carte Fiche médicale : on filtre la liste
  // complète `medicalDocuments` envoyée par le back en enlevant tout libellé
  // commençant par "Questionnaire" (QS standard, simplifié, ou future variante
  // — ces items appartiennent à la carte QS, pas à la fiche médicale).
  // Filtre tolérant à la casse / aux espaces parasites.
  const medicalFormExams = useMemo(() => {
    const docs = globalInfos?.medicalDocuments ?? [];
    return docs.filter(
      (doc) => !doc.trim().toLowerCase().startsWith('questionnaire')
    );
  }, [globalInfos?.medicalDocuments]);

  const [isTriggering, setIsTriggering] = useState(false);

  // Écoute des notifications SignalR pour le processus borrower
  // Déclenche automatiquement un refresh quand une notification est reçue
  useBorrowerProcessSync({
    onProcessUpdated: refresh
  });

  const triggerCallback = useCallback(async () => {
    try {
      const callbackUrl = globalInfos?.partnerCode === 'MUTLOG'
        ? `/backoffice/emprunteurs/mutlog/callback/${globalInfos?.idDossier}/${globalInfos?.numeroAssure}`
        : `/backoffice/devis/emprunteurs/mncap/callback/${globalInfos?.idDossier}/${globalInfos?.numeroAssure}`;

      const response = await client.get(callbackUrl);
      console.log(`Callback ${globalInfos?.partnerCode} success:`, response.data);
      refresh();
      return response.data;
    } catch (error) {
      console.error('Erreur lors du callback:', error);
      throw error;
    }
  }, [globalInfos, refresh]);

  const triggerMNCAPProcess = useCallback(async () => {
    if (!borrowerId || isTriggering) return;

    try {
      setIsTriggering(true);
      const response = await quotesAPI.triggerMNCAPProcess({ borrowerId });
      if (response) {
        refresh();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsTriggering(false);
    }
  }, [borrowerId, isTriggering, refresh]);

  /**
   * Déclenche l'envoi de l'email contenant le lien public pour que le
   * souscripteur dépose ses documents médicaux (Mutlog FM3..FM11).
   *
   * Route officielle exposée par le back :
   *   POST /api/backoffice/emprunteurs/{id}/fiche-medicale/envoyer
   * (le préfixe /api est ajouté par le client axios, on passe juste le chemin).
   */
  const triggerSendMedicalForm = useCallback(async () => {
    if (!borrowerId || isSendingMedicalForm) return;

    try {
      setIsSendingMedicalForm(true);
      await client.post(
        `/backoffice/emprunteurs/${borrowerId}/fiche-medicale/envoyer`
      );
      refresh();
    } catch (error) {
      console.error("Erreur envoi fiche médicale :", error);
    } finally {
      setIsSendingMedicalForm(false);
    }
  }, [borrowerId, isSendingMedicalForm, refresh]);

  // Configuration des cartes basée sur cardStatuses et process
  const process = globalInfos?.process;

  const isGenerating = useMemo(() => {
    return globalInfos?.process?.subscriptionRequest?.isGenerating ?? false;
  }, [globalInfos?.process?.subscriptionRequest?.isGenerating]);

  const isConditionsComplete = globalInfos?.isConditionsComplete ?? false;

  const showActionButton = useMemo(() => {
    return !globalInfos?.process?.subscriptionRequest?.isSend && !isGenerating;
  }, [globalInfos?.process?.subscriptionRequest?.isSend, isGenerating]);

  const cards: CardConfig[] = useMemo(() => {
    if (!cardStatuses) return [];

    const allCards: CardConfig[] = [
      {
        id: 'subscriptionRequest',
        title: "Demande individuelle d'adhésion",
        icon: FileSignature,
        cardStatus: cardStatuses.subscriptionRequest,
        processInfo: process?.subscriptionRequest,
        isVisible: true,
        // Le bouton est affiché si le processus n'a pas démarré et n'est pas en génération
        showActionButton: showActionButton,
        // L'animation de génération est affichée si le document est en cours de génération
        showGeneratingAnimation: isGenerating,
        actionButtonLabel: "Envoyer le bulletin d'adhésion",
        onAction: triggerMNCAPProcess,
        spanFullWidth: !globalInfos?.isCasLemoine
      },
      {
        id: 'medicalSurvey',
        title: 'Questionnaire santé',
        icon: Stethoscope,
        cardStatus: cardStatuses.medicalSurvey,
        processInfo: process?.medicalSurvey,
        isVisible: isMedicalSurveyVisible
      },
      {
        // Position : entre QS et Réponse QS — la fiche médicale est un
        // pré-requis à l'élaboration de la réponse médicale, qui se base
        // sur le QS + les documents médicaux reçus.
        // Le bouton d'envoi est piloté par canSendMedicalForm + isDisabled.
        id: 'medicalForm',
        title: 'Fiche médicale complémentaire',
        icon: Microscope,
        cardStatus: cardStatuses.medicalForm,
        processInfo: undefined,
        isVisible: isMedicalFormVisible
      },
      {
        id: 'medicalSurveyResponse',
        title: 'Réponse questionnaire médical',
        icon: ClipboardCheck,
        cardStatus: cardStatuses.medicalSurveyResponse,
        processInfo: process?.medicalSurveyResponse,
        isVisible: isMedicalSurveyResponseVisible
      },
      {
        id: 'approvedAgreement',
        title: 'Bon pour accord',
        icon: CheckCircle,
        cardStatus: cardStatuses.approvedAgreement,
        processInfo: process?.approvedAgreement,
        isVisible: isApprovedAgreementVisible
      },
      {
        id: 'subscriptionCertificate',
        title: "Certificat d'adhésion",
        icon: Award,
        cardStatus: cardStatuses.subscriptionCertificate,
        processInfo: process?.subscriptionCertificate,
        isVisible: isCertificateVisible
      }
    ];

    return allCards.filter(card => card.isVisible);
  }, [
    cardStatuses,
    process,
    isMedicalSurveyVisible,
    isMedicalSurveyResponseVisible,
    isMedicalFormVisible,
    showActionButton,
    isGenerating,
    globalInfos?.isCasLemoine,
    isApprovedAgreementVisible,
    triggerMNCAPProcess,
    isCertificateVisible
  ]);


  // Si pas de cardStatuses, ne pas afficher la section
  if (!cardStatuses) {
    return null;
  }

  return (
    <CardsSection
      title={'Suivi du dossier'}
      titleAside={
        <div className="flex gap-4">
          {globalInfos?.isCasLemoine && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Scale className="w-4 h-4" />
              Cas Lemoine
            </span>
          )}
          {!globalInfos?.isCasLemoine && window.location.hostname === 'localhost' && (
            <Button outline round onClick={triggerCallback}>
              Callback
            </Button>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {cards.map(card => {
          const cardState = mapApiStatusToCardState(card.cardStatus?.status);
          const config = cardStateConfig[cardState];
          const Icon = card.icon;

          return (
            <GenericCard
              key={card.id}
              className={`flex flex-col ${card.spanFullWidth ? 'md:col-span-2' : ''}`}
            >
              <GenericCard.Header
                icon={
                  <div
                    className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-primary/10`}
                  >
                    <Icon className={`w-5 h-5 text-primary`} />
                  </div>
                }
                titleAside={
                  cardState !== CardState.CRITICAL ? (
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${config.badge}`}
                    >
                      {config.label}
                    </span>
                  ) : null
                }
              >
                {card.title}
              </GenericCard.Header>

              <GenericCard.Body className="flex-1">
                {card.showGeneratingAnimation ? (
                  <GeneratingAnimation />
                ) : card.showActionButton ? (
                  <div className="flex flex-col items-center justify-center h-full gap-2">
                    <Button
                      color="secondary"
                      round
                      onClick={card.onAction}
                      disabled={isTriggering || !isConditionsComplete}
                    >
                      {card.actionButtonLabel}
                    </Button>
                    {!isConditionsComplete && (
                      <p className="text-xs text-amber-600 text-center">
                        Veuillez compléter les conditions particulières avant de générer la demande
                      </p>
                    )}
                  </div>
                ) : card.id === 'subscriptionCertificate' ? (
                  <CertificateInfoDisplay
                    processInfo={card.processInfo as BorrowerProcessStepDto | undefined}
                    partnerCaseStatus={process?.partnerCaseStatus}
                    partnerCaseStatusDetail={process?.partnerCaseStatusDetail}
                  />
                ) : card.id === 'medicalSurveyResponse' ? (
                  <MedicalSurveyResponseInfoDisplay
                    processInfo={card.processInfo as MedicalSurveyResponseProcessDto | undefined}
                    requiresMedicalForm={isMedicalFormVisible}
                  />
                ) : card.id === 'medicalForm' ? (
                  <MedicalFormDisplay
                    exams={medicalFormExams}
                    processInfo={medicalFormProcessInfo}
                    cardStatus={card.cardStatus ?? undefined}
                    canSend={canSendMedicalForm}
                    isSending={isSendingMedicalForm}
                    disabledReason={
                      medicalFormDisabledReason
                        ? MEDICAL_FORM_DISABLED_REASONS[
                            medicalFormDisabledReason
                          ] ?? medicalFormDisabledReason
                        : undefined
                    }
                    onSend={triggerSendMedicalForm}
                  />
                ) : (
                  <ProcessInfoDisplay
                    processInfo={card.processInfo as BorrowerProcessStepDto | BorrowerMedicalProcessDto | undefined}
                    isMedical={card.id === 'medicalSurvey'}
                  />
                )}
              </GenericCard.Body>
            </GenericCard>
          );
        })}
      </div>
    </CardsSection>
  );
};

export default BorrowerProcessTracker;
